import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Sub, SubContent, SubTrigger } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { useBalance } from 'wagmi';
import { faChevronDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@components/basics/Dropdowns/GlobalDropdown';
import { LoadingSpinner } from '@components/basics/LoadingSpinner';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { formatWeiToEthForDisplay, humanReadableChainName } from '@utils/helpers';
import { Balances } from './BalancesDropdown';

export const BalanceTrigger = ({
  target,
  variant,
}: {
  target: 'desktop' | 'mobile';
  variant: 'nav-menu' | 'left-nav';
}) => {
  const { authUserAddress } = useAuth();
  const {
    activeChainIdWithFallback: { current: activeChainId },
  } = useConnectedAuth();
  const [openDropdown, setOpenDropdown] = React.useState(false);

  const {
    data: balanceData,
    isError: balanceError,
    isLoading: balanceLoading,
  } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId: activeChainId,
  });

  const disabledButton = balanceLoading || balanceError;

  const trigger = React.useMemo(
    () => (
      <div className="flex h-11 w-full cursor-pointer flex-col gap-y-1 md:h-[54px] md:px-3 md:pt-2 md:hover:bg-neutral50">
        {disabledButton ? (
          <div className="flex h-[26px] w-full items-center justify-center md:h-10">
            <LoadingSpinner className="aspect-square w-4" />
          </div>
        ) : (
          <>
            <p className="min-h-[16px] text-left text-base-m text-base400 md:text-base-s">
              {humanReadableChainName(activeChainId)}
            </p>
            <div className="flex w-full items-center justify-between font-base text-base900">
              <h3 className="font-title text-title-m font-medium ">
                {balanceData ? (
                  <>
                    {formatWeiToEthForDisplay({
                      weiValue: balanceData.value.toString(),
                      maxDecimals: 4,
                    })}{' '}
                    ETH
                  </>
                ) : (
                  <>Unable to load</>
                )}
              </h3>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  'text-base-m text-base400',
                  openDropdown && 'rotate-180 transform transition-transform',
                )}
              />
            </div>
          </>
        )}
      </div>
    ),
    [disabledButton, activeChainId, balanceData, openDropdown],
  );

  if (target === 'desktop') {
    return (
      <Sub open={openDropdown}>
        <SubTrigger
          onClick={() => {
            setOpenDropdown(prev => !prev);
          }}
          disabled={disabledButton}
          className="outline-none focus:outline-none"
        >
          {trigger}
        </SubTrigger>
        <SubContent className="absolute right-2 top-14 z-tooltip rounded-md border-0 bg-white shadow-primary">
          <Balances variant={variant} />
        </SubContent>
      </Sub>
    );
  }

  return (
    <div className="flex w-full md:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger className="w-full" disabled={disabledButton}>
          {trigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent disablePortal className="border-0 shadow-primary">
          <Balances variant={variant} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
